import { acceptHMRUpdate, defineStore } from 'pinia'
import api from '@api'
import type {
  LanguageEnum,
  PasswordChange,
  PasswordReset,
  PasswordResetConfirm, ResendEmailVerification,
  TokenVerify,
  UserDetails,
  VerifyEmail,
  Workspace,
  PatchedWorkspaceUpdate, WorkspaceLogo, ForceLogoutRequest,
} from '@apiTypes'
import { mapWorkspaceOnMembers } from '@/utils/user.utils'
import i18n, { defaultLanguage, isLanguageSupported } from '@/plugins/i18n'

let workspaceRefreshSeq = 0

interface AuthState {
  isNewRegistration: boolean
  isNewCompany: boolean
  currentUser: UserDetails | null
  currentWorkspace: Workspace | null
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    isNewRegistration: false,
    isNewCompany: false,
    currentUser: null,
    currentWorkspace: null,
  }),
  getters: {
    getCurrentUserFullName: state => {
      return [state.currentUser?.firstName, state.currentUser?.lastName].filter(v => !!v).join(' ')
    },
    getCurrentLanguage: state => {
      const locale = toValue(i18n.global.locale)

      return state.currentUser?.preferredLanguage || (isLanguageSupported(locale) ? locale : defaultLanguage)
    },
    getDisplayName: state => {
      return [state.currentUser?.firstName, state.currentUser?.lastName].filter(v => !!v).join(' ') || state.currentUser?.email
    },
    getDisplayShortName: state => {
      return state.currentUser?.firstName || state.currentUser?.lastName || state.currentUser?.email
    },
    getWorkspaceDisplayName: state => {
      return state.currentWorkspace?.name || ''
    },
    getWorkspaceCurrency: state => {
      return state.currentWorkspace?.currency || ''
    },
    getWorkspacePreferences: state => {
      return state.currentWorkspace?.preferences
    },
    getCurrentAuthProvider: state => {
      return state.currentWorkspace?.preferences?.authProvider || ''
    },
  },
  actions: {
    clear() {
      workspaceRefreshSeq++
      this.currentUser = null
      this.currentWorkspace = null
    },
    setIsNewRegistration(isNewCompany = true) {
      this.isNewRegistration = true
      this.isNewCompany = isNewCompany
    },
    clearIsNewRegistration() {
      this.isNewRegistration = false
      this.isNewCompany = false
    },
    setCurrentUser(user: UserDetails, updateWorkspace: boolean = true) {
      this.currentUser = user
      if (updateWorkspace)
        this.currentWorkspace = user.workspace
    },
    setCurrentWorkspace(workspace: Workspace) {
      workspaceRefreshSeq++
      this.currentUser = this.currentUser && {
        ...this.currentUser,
        members: mapWorkspaceOnMembers(workspace, this.currentUser.members),
      } // we are mapping current workspace member to prevent unnecessary request on the server
      this.currentWorkspace = workspace
    },
    setPassword(data: PasswordChange) {
      return api.auth.authPasswordChangeCreate(data)
    },
    resetPassword(data: PasswordReset) {
      return api.auth.authPasswordResetCreate(data, { skipAuthToken: true })
    },
    verifyToken(data: TokenVerify) {
      return api.auth.authTokenVerifyCreate(data, { skipAuthToken: true, skipAuthRefresh: true, skipLogout: true })
    },
    confirmResetPassword(data: PasswordResetConfirm) {
      return api.auth.authPasswordResetConfirmCreate(data, { skipAuthToken: true })
    },
    confirmEmail(data: VerifyEmail) {
      return api.auth.authRegisterVerifyEmailCreate(data, { skipAuthToken: true })
    },
    resendConfirmationEmail(data: ResendEmailVerification) {
      return api.auth.authRegisterResendEmailCreate(data)
    },
    fetchCurrentUser(store: boolean = true) {
      return api.auth.authUserRetrieve({ skipLogout: true, skipAuthRefresh: true }).then(response => {
        if (store)
          this.setCurrentUser(response.data)

        return response
      })
    },
    logoutAllUsers(workspaceId: number | undefined, data: ForceLogoutRequest) {
      if (workspaceId === undefined)
        return

      return api.workspace.workspaceLogoutAllUsersCreate({ id: workspaceId }, data)
    },
    updateWorkspace(data: PatchedWorkspaceUpdate) {
      const previousCurrency = this.currentWorkspace?.currency

      return api.workspace.workspacePartialUpdate({ id: this.currentWorkspace?.id ?? 0 }, data).then(response => {
        this.setCurrentWorkspace(response.data)

        if (previousCurrency !== undefined && response.data.currency !== previousCurrency)
          void this.refreshCurrentWorkspace()

        return response
      })
    },
    refreshCurrentWorkspace(): Promise<Workspace | null> {
      const workspaceId = this.currentWorkspace?.id
      if (!workspaceId)
        return Promise.resolve(null)

      const requestSeq = ++workspaceRefreshSeq

      // Resolves to the applied workspace, or null when nothing was read (HTTP error, superseded
      // response, no workspace) — callers must not treat a bare resolve as a confirmed read.
      return api.workspace.workspaceRetrieve({ id: workspaceId })
        .then(response => {
          if (requestSeq !== workspaceRefreshSeq || this.currentWorkspace?.id !== workspaceId)
            return null

          this.setCurrentWorkspace(response.data)

          return response.data
        })
        .catch((error: unknown) => {
          console.error('Failed to refresh current workspace', error)

          return null
        })
    },
    uploadWorkspaceLogo(imageFile: File) {
      return api.workspace.workspaceUploadLogoCreate({ id: this.currentWorkspace?.id ?? 0 }, { logo: imageFile } as unknown as WorkspaceLogo, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    },
    downloadSampleDocument() {
      return api.workspace.workspaceDownloadSampleTransportOrderRetrieve({ id: this.currentWorkspace?.id ?? 0 }, { format: 'blob' })
    },
    createWorkspace(data: Workspace) {
      return api.workspace.workspaceCreate(data)
    },
    updateUser(data: UserDetails) {
      return api.auth.authUserUpdate(data).then(response => {
        this.setCurrentUser(response.data)

        return response
      })
    },
    updateLanguage(preferredLanguage: LanguageEnum) {
      return api.auth.authUserPartialUpdate({ preferredLanguage }).then(response => {
        this.setCurrentUser(response.data)

        return response
      })
    },
  },
})

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
